import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import TemplateCard from "./TemplateCard";
import "./TemplateGallery.css";
import UpgradeModal from "../Components/Master/UpgradeModal";
import Header from "../Common/Layout/Header";
import { useAuth } from "../Common/Configurations/AuthContext";
import WebsiteTabs from "../Components/AllTabs/WebsiteTabs";
import TemplateSetupWizard from "./TemplateSetupWizard";

function TemplateGallery() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  const [wizardOpen, setWizardOpen] = useState(false);
  const[selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [assignedTemplateId, setAssignedTemplateId] = useState(null);

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (user && !authLoading) {
      const fetchData = async () => {
        try {
          // 1. Fetch all templates
          const response = await apiService.getAll(config.GETALLTEMPLATES);
          setTemplates(response.data || response);

          // 2. Fetch the assigned template for the current company
          if (user?.cid) {
            try {
              const assignedRes = await apiService.getAll(`${config.GETTEMPLATEBYCOMPANYID}/${user.cid}`);
              console.log("Assigned Template Response:", assignedRes);
              const templateId = assignedRes?.data || assignedRes;
              
              if (templateId) {
                setAssignedTemplateId(templateId);
              }
            } catch (assignError) {
              console.log("No template assigned for this company yet.");
            }
          }
        } catch (error) {
          console.error("Failed to fetch templates:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else if (!authLoading) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  const handlePreview = (templateId) => {
    window.open(`/template-preview/${templateId}`, "_blank");
  };

  const handleSelectInitiate = (template) => {
    setSelectedTemplateId(template.templateId); 
    setWizardOpen(true); 
  };

 const handleFinalSubmit = async (customizedFormData) => {
    setWizardOpen(false); 
    setAssigning(true);   

    try {
      // 💡 NOTE: customizedFormData is ALREADY a complete FormData object 
      // coming from TemplateSetupWizard, containing CompanyId, TemplateId, LogoFile, and all JSONs.
      
      // Send the FormData directly to the API
      const response = await apiService.post(config.ASSIGNTEMPLATE, customizedFormData);

      if (response) {
        alert("Congratulations! Your customized website has been successfully applied.");
        // Instantly update the assigned template in the UI
        setAssignedTemplateId(selectedTemplateId);
      }
    } catch (error) {
      console.error("Failed to assign template:", error);
      alert("Something went wrong while assigning the template. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // 🔴 NEW LOGIC: Split the templates into two categories
  const activeTemplate = templates.find(t => t.templateId === assignedTemplateId);
  const otherTemplates = templates.filter(t => t.templateId !== assignedTemplateId);

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      
      <TemplateSetupWizard 
        show={wizardOpen} 
        onClose={() => setWizardOpen(false)} 
        onSave={handleFinalSubmit}
        user={user}
        templateId={selectedTemplateId} 
      />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card border-0 shadow-sm">
              <div className="p-2">
                <WebsiteTabs pageName="WEBSITETEMPLATES" />
              </div>
              <div className="card-body">
                
                {assigning && (
                  <div className="alert alert-info text-center mb-4 shadow-sm" style={{borderRadius: '10px'}}>
                    <div className="spinner-border spinner-border-sm me-2"></div>
                    <strong>Generating Website:</strong> Setting up your template with your customized branding...
                  </div>
                )}

                {/* 🔴 SECTION 1: Active / Selected Template */}
                {activeTemplate && (
                  <div className="mb-1 bg-light p-1 rounded border">
                    <h4 className="mb-4 fw-bold text-primary">
                      <i className="bi bi-star-fill text-warning me-2"></i> 
                      Current Active Template
                    </h4>
                    <div className="row">
                      <TemplateCard
                        key={activeTemplate.templateId}
                        template={activeTemplate}
                        onPreview={() => handlePreview(activeTemplate.templateId)}
                        onSelect={() => handleSelectInitiate(activeTemplate)}
                        isAssigned={true}
                      />
                    </div>
                  </div>
                )}

                {/* 🔴 SECTION 2: Other Available Templates */}
                <div>
                  <h4 className="mb-4 fw-bold text-secondary">
                    {activeTemplate ? "More Templates to View" : "Available Templates"}
                  </h4>
                  <div className="row">
                    {otherTemplates.length > 0 ? (
                      otherTemplates.map((template) => (
                        <TemplateCard
                          key={template.templateId}
                          template={template}
                          onPreview={() => handlePreview(template.templateId)}
                          onSelect={() => handleSelectInitiate(template)}
                          isAssigned={false} 
                        />
                      ))
                    ) : (
                      <div className="col-12">
                         <p className="text-center py-5 text-muted">No other templates available at the moment.</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <UpgradeModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
}

export default TemplateGallery;